// BookingCreator.js - Updated to handle different booking types properly
import React, { useState, useEffect, useCallback } from 'react';
import { Box, Grid } from '@mui/material';
import dayjs from 'dayjs';
import { getVenueCommunityBookings } from 'api';

// Import our components
import BookingTypeSelector from './bookingTypeSelector';
import FilterComponent from './filterComponent';
import ResourceList from './resourceList';
import CalendarSection from './calendarSection';
import TimeSlotSection from './timeSlotSection';
//import DeskSelector from './deskSelector';
import BookingSummary from './bookingSummary';

// Function to extract resources from venue structure
const extractResourcesFromVenue = (venue, bookingType) => {
    if (!venue || !venue.floors) return [];

    let extractedResources = [];

    // Go through each floor
    venue.floors.forEach(floor => {
        // Go through each work area in the floor
        floor.work_areas?.forEach(workArea => {
            if (bookingType === 'MR' && workArea.meeting_rooms) {
                // Extract meeting rooms
                workArea.meeting_rooms.forEach(room => {
                    // Add floor and work area info to the room
                    extractedResources.push({
                        ...room,
                        floorName: floor.name,
                        workAreaName: workArea.name,
                        // If these properties don't exist, we provide defaults
                        hourly_rate: room.hourly_rate || 15,
                        full_day_price: room.full_day_price || 162,
                        type: 'MR'
                    });
                });
            } else if (bookingType === 'HD' && workArea.status == 'AC' && workArea.hot_desks && workArea.hot_desks.length > 0) {
                // For hot desks, we store the work area that contains them
                extractedResources.push({
                    workAreaName: workArea.name,
                    workAreaSlug: workArea.slug,
                    floorName: floor.name,
                    hotDesksCount: workArea.hot_desks.length,
                    images: workArea.images || [],
                    type: 'HD'
                });
            }
        });
    });

    return extractedResources;
};

const BookingCreator = ({
    slug,
    venue,
    defaultDate = null,
    onBookingComplete = () => { },
    availableBookingTypes = ['HD', 'MR'],
    initialBookingType = null,
    onBookingTypeChange = null,
    resources = null,
    loadingResources = false,
    onDateChange = null,
    preparingBooking = false,
    spaces = 1,
    selectedPrice = null,
    totalPrice = 0,
    totalPriceVAT = 0,
    compactView = false,
    viewResourceDetails = () => { },
    externalSelectedResource = null,
    enabledHD = true,
    enabledMR = true,
    defaultCommunity = null,
}) => {
    // State variables
    const [activeStep, setActiveStep] = useState(compactView ? 3 : 1);
    const [bookingType, setBookingType] = useState(initialBookingType || availableBookingTypes[0] || 'HD');
    const [selectedResource, setSelectedResource] = useState(null);
    const [selectedDateLocal, setSelectedDateLocal] = useState(defaultDate || null);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
    const [spacesLocal, setSpacesLocal] = useState(spaces);
    const [filterExpanded, setFilterExpanded] = useState(false);
    const [calculatedPrice, setCalculatedPrice] = useState(totalPrice);
    const [calculatedPriceVAT, setCalculatedPriceVAT] = useState(totalPriceVAT);
    const [processedResourceId, setProcessedResourceId] = useState(null);
    const [selectedCommunity, setSelectedCommunity] = useState(defaultCommunity);


    // NEW: Store the selected price object from calendar for hot desks
    const [hotDeskPriceObject, setHotDeskPriceObject] = useState(null);

    // Filter states
    const [capacityFilter, setCapacityFilter] = useState([1, 10]);
    const [amenitiesFilter, setAmenitiesFilter] = useState([]);

    // Local resources state
    const [localResources, setLocalResources] = useState({
        meetingRooms: [],
        hotDesks: []
    });

    // New state to track if we should skip resource selection
    const [skipResourceSelection, setSkipResourceSelection] = useState(false);

    // Loading states
    const [loadingAvailability, setLoadingAvailability] = useState(false);

    // Availability data
    const [availability, setAvailability] = useState({
        meetingRooms: {},
        hotDesks: {}
    });

    // Community bookings state
    const [communityBookingsByDate, setCommunityBookingsByDate] = useState({});

    // Fetch community bookings for this venue
    const fetchCommunityBookings = useCallback(async () => {
        if (!slug) return;
        
        try {
            const today = dayjs();
            const response = await getVenueCommunityBookings(slug, {
                start_date: today.format('YYYY-MM-DD'),
                end_date: today.add(60, 'day').format('YYYY-MM-DD'),
            });
            
            if (response?.data) {
                setCommunityBookingsByDate(response.data.bookings_by_date || {});
            }
        } catch (error) {
            console.error('Error fetching community bookings:', error);
        }
    }, [slug]);

    // Effect to fetch community bookings on mount
    useEffect(() => {
        fetchCommunityBookings();
    }, [fetchCommunityBookings]);

    // Effect to initialize based on venue data and provided resources
    useEffect(() => {
        if (venue) {
            let resourcesData = [];

            // First, try to use the provided resources prop
            if (resources && resources.length > 0) {
                resourcesData = resources;
            }
            // If no resources provided, extract them from venue
            else if (venue.floors) {
                resourcesData = extractResourcesFromVenue(venue, bookingType);
                // check if we have HD in resourceData array, if notm setEnabledHD(false)


            }

            if (resourcesData && resourcesData.length > 0) {
                // Format the resources based on booking type
                if (bookingType === 'MR') {
                    const meetingRooms = resourcesData.filter(r =>
                        r.type === 'MR' || // For resources extracted from venue
                        r.capacity !== undefined // For directly provided meeting rooms
                    );

                    setLocalResources({
                        meetingRooms,
                        hotDesks: []
                    });

                    // If there's only one meeting room, auto-select it
                    if (meetingRooms.length === 1) {
                        setSelectedResource(meetingRooms[0]);
                        setActiveStep(3); // Skip to date selection
                    }
                } else { // Hot Desks
                    // Filter to get only hot desk work areas
                    const hotDeskAreas = resourcesData.filter(r =>
                        r.type === 'HD'
                    );

                    setLocalResources({
                        meetingRooms: [],
                        hotDesks: hotDeskAreas
                    });

                    // If there's only one hot desk area, auto-select it
                    if (hotDeskAreas.length === 1) {
                        setSelectedResource(hotDeskAreas[0]);
                        setActiveStep(3); // Skip to date selection
                    }
                }
            } else {
                // Reset local resources when switching to a booking type with no resources
                setLocalResources({
                    meetingRooms: [],
                    hotDesks: []
                });
                setSelectedResource(null);
            }
        }
    }, [venue, resources, bookingType]);

    // Effect to handle external selected resource
    useEffect(() => {
        if (!externalSelectedResource) return;

        // Create a unique identifier for this resource to prevent processing the same resource multiple times
        const resourceIdentifier = externalSelectedResource._timestamp ||
            externalSelectedResource.id ||
            externalSelectedResource.slug;

        // Skip if we've already processed this exact resource
        if (resourceIdentifier === processedResourceId) return;

        // Determine the booking type based on resource properties
        let resourceType = 'MR'; // Default to meeting room

        // Check if it's a hot desk by looking for characteristic properties
        if (externalSelectedResource.hotDesksCount ||
            externalSelectedResource.workAreaSlug) {
            resourceType = 'HD';
        }
        // Check if it's a meeting room by looking for characteristic properties
        else if (externalSelectedResource.capacity !== undefined ||
            externalSelectedResource.hourly_rate !== undefined ||
            externalSelectedResource.hourly_slots !== undefined) {
            resourceType = 'MR';
        }

        // Set the determined resource type
        setBookingType(resourceType);

        // If the resource doesn't have a type property, add it
        const updatedResource = {
            ...externalSelectedResource,
            type: resourceType
        };

        setSelectedResource(updatedResource);
        setActiveStep(3); // Move to date selection
        setSkipResourceSelection(true); // Set this flag to skip resource selection

        // Mark this resource as processed
        setProcessedResourceId(resourceIdentifier);

        // Notify parent of booking type change if callback exists
        if (onBookingTypeChange) {
            onBookingTypeChange(resourceType);
        }

    }, [externalSelectedResource, onBookingTypeChange, processedResourceId]);

    useEffect(() => {
        setBookingType(initialBookingType);
    }, [initialBookingType]);

    // Effect to fetch availability when resource and date are selected
    useEffect(() => {
        if (selectedResource && selectedDateLocal) {
            setLoadingAvailability(true);

            // Simulate API call
            setTimeout(() => {
                // Fetch availability data based on resource and date
                if (bookingType === 'MR') {
                    // Generate mock hourly slots for meeting rooms
                    const hourlySlots = selectedResource.hourly_slots || Array.from({ length: 18 }, (_, i) => ({
                        start_time: `${Math.floor(i / 2) + 9}:${i % 2 === 0 ? '00' : '30'}`,
                        end_time: `${Math.floor((i + 1) / 2) + 9}:${(i + 1) % 2 === 0 ? '00' : '30'}`,
                        is_available: Math.random() > 0.3,
                        duration_minutes: 30,
                        is_full_slot: true,
                        price: selectedResource.hourly_rate / 2 || 15,
                        slot_price: (selectedResource.hourly_rate / 2 || 15) / 2,
                        slot_price_vat: ((selectedResource.hourly_rate / 2 || 15) / 2) * 1.2
                    }));

                    setAvailability(prev => ({
                        ...prev,
                        meetingRooms: {
                            ...prev.meetingRooms,
                            [selectedResource.id]: {
                                ...prev.meetingRooms[selectedResource.id],
                                [selectedDateLocal.format('YYYY-MM-DD')]: hourlySlots
                            }
                        }
                    }));
                } else {
                    // Hot desk availability
                    const hotDeskAvailability = {
                        price: selectedDateLocal.day() === 0 || selectedDateLocal.day() === 6 ? 21 : 10,
                        available: Math.floor(Math.random() * selectedResource.hotDesksCount) + 1,
                        original_price: 25
                    };

                    setAvailability(prev => ({
                        ...prev,
                        hotDesks: {
                            ...prev.hotDesks,
                            [selectedResource.workAreaSlug]: {
                                ...prev.hotDesks[selectedResource.workAreaSlug],
                                [selectedDateLocal.format('YYYY-MM-DD')]: hotDeskAvailability
                            }
                        }
                    }));
                }

                setLoadingAvailability(false);
            }, 700);
        }
    }, [selectedResource, selectedDateLocal, bookingType]);

    // Add a useEffect to calculate prices based on selections
    useEffect(() => {
        if (!selectedResource || !selectedDateLocal) {
            setCalculatedPrice(0);
            setCalculatedPriceVAT(0);
            return;
        }

        if (bookingType === 'MR' && selectedTimeSlot) {
            // For meeting rooms - use the selected time slot price
            if (selectedTimeSlot.price) {
                setCalculatedPrice(parseFloat(selectedTimeSlot.price));
                setCalculatedPriceVAT(selectedTimeSlot.price_vat
                    ? parseFloat(selectedTimeSlot.price_vat)
                    : parseFloat(selectedTimeSlot.price) * 1.2);
            } else if (selectedTimeSlot.slot_price) {
                setCalculatedPrice(parseFloat(selectedTimeSlot.slot_price));
                setCalculatedPriceVAT(selectedTimeSlot.slot_price_vat
                    ? parseFloat(selectedTimeSlot.slot_price_vat)
                    : parseFloat(selectedTimeSlot.slot_price) * 1.2);
            }
        } else if (bookingType === 'HD') {
            // For hot desks - calculate based on number of spaces and hot desk price object
            if (hotDeskPriceObject) {
                const basePrice = parseFloat(hotDeskPriceObject.price) * spacesLocal;
                setCalculatedPrice(basePrice);

                const vatPrice = parseFloat(hotDeskPriceObject.price_vat) * spacesLocal;
                setCalculatedPriceVAT(vatPrice);
            } else {
                // Fallback to availability if no price object
                const availability = getHotDeskAvailability();
                if (availability) {
                    const basePrice = availability.price * spacesLocal;
                    setCalculatedPrice(basePrice);
                    setCalculatedPriceVAT(availability.price_vat
                        ? availability.price_vat * spacesLocal
                        : basePrice * 1.2);
                }
            }
        }
    }, [bookingType, selectedResource, selectedDateLocal, selectedTimeSlot, spacesLocal, hotDeskPriceObject]);

    // Handler for booking type change
    const handleBookingTypeChange = (event, newType) => {
        if (newType !== null) {
            setBookingType(newType);
            setSelectedResource(null);
            setSelectedTimeSlot(null);
            setHotDeskPriceObject(null); // Reset hot desk price when changing type
            setActiveStep(compactView ? 3 : 1);
            setSkipResourceSelection(false); // Reset this flag when changing booking type

            // Call parent handler if provided
            if (onBookingTypeChange) {
                onBookingTypeChange(newType);
            }
        }
    };

    // Handler for resource selection
    const handleResourceSelect = (resource) => {
        setSelectedResource(resource);
        setActiveStep(3); // Move to date selection
    };

    // Handler for viewing resource details
    const handleViewResourceDetails = (resource) => {
        // Add a timestamp to ensure the reference changes
        const resourceWithTimestamp = {
            ...resource,
            _timestamp: Date.now()
        };

        // Call the parent component's callback to open the resource detail modal
        if (viewResourceDetails) {
            viewResourceDetails(resourceWithTimestamp);
        }
    };

    // Handler for date selection from calendar
    const handleDateSelect = (priceObject) => {
        setSelectedDateLocal(dayjs(priceObject.start));

        // For hot desks, store the price object for later use
        if (bookingType === 'HD') {
            setHotDeskPriceObject(priceObject);
        }

        // Call parent handler if provided
        if (onDateChange) {
            onDateChange(dayjs(priceObject.start));
        }

        if (bookingType === 'MR') {
            setActiveStep(4); // Move to time slot selection for meeting rooms
        }
    };

    const handleTimeSlotSelect = (room, slot) => {
        // Make sure the slot has the necessary price information
        if (!slot) {
            // If slot is null, clear the selection and reset prices
            setSelectedTimeSlot(null);
            setCalculatedPrice(0);
            setCalculatedPriceVAT(0);
            return;
        }
        const updatedSlot = {
            ...slot,
            price: slot.price || slot.slot_price || 0,
            price_vat: slot.price_vat || slot.slot_price_vat || (slot.price || slot.slot_price || 0) * 1.2
        };

        setSelectedTimeSlot(updatedSlot);

        // Update calculated prices based on the selected time slot
        if (updatedSlot?.price) {
            setCalculatedPrice(parseFloat(updatedSlot.price));
            setCalculatedPriceVAT(updatedSlot.price_vat
                ? parseFloat(updatedSlot.price_vat)
                : parseFloat(updatedSlot.price) * 1.2);
        }
    };


    // Handler for booking confirmation
    const handleConfirmBooking = () => {
        // Create booking data based on type
        const bookingData = {
            bookingType,
            resource: selectedResource,
            date: selectedDateLocal || defaultDate,
            spaces: spacesLocal || spaces,
            communitySlug: selectedCommunity
        };

        // Add type-specific data
        if (bookingType === 'MR') {
            // For meeting rooms - include time slot info
            bookingData.timeSlot = selectedTimeSlot;
            bookingData.totalPrice = calculatedPrice;
            bookingData.totalPriceVAT = calculatedPriceVAT;
        } else if (bookingType === 'HD') {
            // For hot desks - include price object and correct prices
            bookingData.priceObject = hotDeskPriceObject;
            bookingData.timeSlot = null; // Hot desks don't have time slots

            // Use hot desk price if available, otherwise use calculated price
            if (hotDeskPriceObject) {
                bookingData.totalPrice = parseFloat(hotDeskPriceObject.price) * spacesLocal;
                bookingData.totalPriceVAT = parseFloat(hotDeskPriceObject.price_vat) * spacesLocal;
            } else {
                bookingData.totalPrice = calculatedPrice;
                bookingData.totalPriceVAT = calculatedPriceVAT;
            }
        }

        // Call the onBookingComplete callback with the booking data
        onBookingComplete(bookingData);
    };

    // Helper to get current hot desk availability
    const getHotDeskAvailability = () => {
        if (!selectedResource || !selectedDateLocal) return null;

        const dateStr = selectedDateLocal.format('YYYY-MM-DD');
        return availability.hotDesks[selectedResource.workAreaSlug]?.[dateStr];
    };

    // Helper to get current meeting room time slots
    const getMeetingRoomTimeSlots = () => {
        if (!selectedResource || !selectedDateLocal) return [];

        // First check if the room has hourly_slots directly attached
        if (selectedResource.hourly_slots && Array.isArray(selectedResource.hourly_slots)) {
            return selectedResource.hourly_slots;
        }

        // If not, check the availability state
        const dateStr = selectedDateLocal.format('YYYY-MM-DD');
        const resourceId = selectedResource.id || selectedResource.slug;
        const availabilitySlots = availability.meetingRooms[resourceId]?.[dateStr] || [];

        return availabilitySlots;
    };

    // Filter resources based on current filters
    const getFilteredResources = () => {
        if (bookingType === 'MR') {
            return localResources.meetingRooms.filter(room => {
                // Filter by capacity
                const capacityMatch = (room.capacity || 1) >= capacityFilter[0] &&
                    (room.capacity || 1) <= capacityFilter[1];

                // Filter by amenities
                const amenitiesMatch = amenitiesFilter.length === 0 ||
                    amenitiesFilter.every(amenity =>
                        room.features && Array.isArray(room.features) && room.features.some(feature =>
                            typeof feature === 'string' && feature.toLowerCase().includes(amenity.toLowerCase())
                        )
                    );
                return capacityMatch && amenitiesMatch;
            });
        } else {
            return localResources.hotDesks.filter(area => {
                // For hot desks, just filter by available desks
                return (area.hotDesksCount || 1) >= capacityFilter[0];
            });
        }
    };

    // Get filtered resources based on current filters
    const filteredResources = getFilteredResources();

    // The content to render - we'll use the same for both compact and full views
    const bookingContent = (
        <>
            {/* Booking type selector */}
            {availableBookingTypes.length > 1 && (
                <BookingTypeSelector
                    enabledHD={enabledHD}
                    enabledMR={enabledMR}
                    bookingType={bookingType}
                    handleBookingTypeChange={handleBookingTypeChange}
                    availableBookingTypes={availableBookingTypes}
                    compact={compactView}
                />
            )}

            <Grid container spacing={3}>
                <Grid item xs={12} md={compactView ? 12 : 8}>
                    {/* WHAT: Capacity and Amenities Filter - Only show if there are multiple resources to filter */}
                    {/*                     {filteredResources.length > 1 && (
                        <FilterComponent
                            bookingType={bookingType}
                            capacityFilter={capacityFilter}
                            setCapacityFilter={setCapacityFilter}
                            amenitiesFilter={amenitiesFilter}
                            setAmenitiesFilter={setAmenitiesFilter}
                            filterExpanded={filterExpanded}
                            setFilterExpanded={setFilterExpanded}
                        />
                    )} */}

                    {/* WHICH: Resource Selection - Always show to display the selected resource */}
                    <ResourceList
                        bookingType={bookingType}
                        loadingResources={loadingResources}
                        resources={filteredResources}
                        selectedResource={selectedResource}
                        handleResourceSelect={handleResourceSelect}
                        onViewDetails={handleViewResourceDetails}
                        autoSelectSingle={filteredResources.length === 1}
                    />

                    {/* WHEN: Date Selection */}
                    <CalendarSection
                        slug={slug}
                        workAreaSlug={bookingType === 'HD' ? selectedResource?.workAreaSlug : null}
                        resourceSlug={bookingType === 'MR' ? selectedResource?.slug : null}
                        selectedDate={selectedDateLocal}
                        selectedPrice={bookingType === 'HD' ? hotDeskPriceObject : selectedPrice}
                        spaces={spacesLocal}
                        handleDateSelect={handleDateSelect}
                        loadingData={loadingAvailability}
                        bookingType={bookingType}
                        inPaper={true}
                        showPrices={bookingType === 'HD'} // Only show prices for hot desks
                        doBooking={handleConfirmBooking}
                        selectedResource={selectedResource}
                        venue={venue}
                        selectedCommunity={selectedCommunity}
                        onCommunityChange={setSelectedCommunity}
                        communityBookingsByDate={communityBookingsByDate}
                    />


                    {/* AVAILABILITY: Meeting Room Time Slots */}
                    {bookingType === 'MR' && selectedResource && selectedResource?.type !== 'HD' && selectedDateLocal && (
                        <TimeSlotSection
                            loadingAvailability={loadingAvailability}
                            timeSlots={getMeetingRoomTimeSlots()}
                            handleTimeSlotSelect={handleTimeSlotSelect}
                            room={selectedResource}
                            selectedSlot={selectedTimeSlot}
                            selectedDate={selectedDateLocal}
                            doBooking={handleConfirmBooking}
                        />
                    )}

                    {/* For Hot Desks: Number of Desks Selector */}
                    {/*                     {bookingType === 'HD' && selectedResource && selectedDateLocal && (
                        <DeskSelector
                            loadingAvailability={loadingAvailability}
                            spaces={spacesLocal}
                            setSpaces={setSpacesLocal}
                            // Use hotDeskPriceObject if available, otherwise fallback to availability
                            availabilityInfo={hotDeskPriceObject || getHotDeskAvailability()}
                        />
                    )} */}
                </Grid>

                {/* In compact view, booking summary is full width. In full view, it's in the sidebar */}
                {/* <Grid item xs={12} md={compactView ? 12 : 4}>
                    <BookingSummary
                        bookingType={bookingType}
                        selectedResource={selectedResource}
                        selectedDate={selectedDateLocal}
                        selectedTimeSlot={selectedTimeSlot}
                        spaces={spacesLocal}
                        // Use correct price based on booking type
                        totalPrice={
                            bookingType === 'HD' && hotDeskPriceObject
                                ? parseFloat(hotDeskPriceObject.price) * spacesLocal
                                : calculatedPrice
                        }
                        totalPriceVAT={
                            bookingType === 'HD' && hotDeskPriceObject
                                ? parseFloat(hotDeskPriceObject.price_vat) * spacesLocal
                                : calculatedPriceVAT
                        }
                        preparingBooking={preparingBooking}
                        canBook={canBook()}
                        handleConfirmBooking={handleConfirmBooking}
                        bookingStatusMessage={getBookingStatusMessage()}
                    />
                </Grid> */}
            </Grid>
        </>
    );

    // Return the content with appropriate wrapper for each view
    if (compactView) {
        return (
            <Box sx={{ mb: 2 }}>
                {bookingContent}
            </Box>
        );
    } else {
        return (
            <Box sx={{ mb: 4 }}>
                {bookingContent}
            </Box>
        );
    }
};

export default BookingCreator;